// PowMixParensTask.js
// Miks z nawiasem: (a^m •/ a^p)^k  •/  a^n  → najpierw w nawiasie (suma/różnica),
// potem potęga potęgi (mnożenie wykładnika przez k), na końcu mnożenie/dzielenie
// Wersja tylko z wykładnikami naturalnymi (brak ujemnych w treści i w wyniku)
// + forma dzielenia losowana w generateTask (brak Math.random w render)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const factorLatex = (a, exp) => (exp === 1 ? `${a}` : `${a}^{${exp}}`);

// Deterministyczna forma dzielenia wewnątrz nawiasu: a^m : a^p
// → przy useFraction=true: "\dfrac{a^m}{a^p}", inaczej "a^m : a^p"
const innerDivisionLatex = (a, m, p, useFraction) => {
  const num = factorLatex(a, m);
  const den = factorLatex(a, p);
  if (useFraction) {
    return `\\dfrac{${num}}{${den}}`;
  }
  return `${num}\\,:\\,${den}`;
};

// Deterministyczna forma dzielenia na zewnątrz: ( ... )^k : a^n
// → przy useFraction=true: "\dfrac{( ... )^k}{a^n}", inaczej "( ... )^k : a^n"
const outerDivisionLatex = (numeratorLatex, denominatorLatex, useFraction) => {
  if (useFraction) {
    return `\\dfrac{${numeratorLatex}}{${denominatorLatex}}`;
  }
  return `${numeratorLatex}\\,:\\,${denominatorLatex}`;
};

export function generateTask() {
  const basePool = [2, 3, 4, 5, 6, 7, 8, 9];
  const a = pick(basePool);

  // losujemy warianty
  const innerOp = pick(["mul", "div"]); // w nawiasie: mnożenie lub dzielenie
  const outerOp = pick(["mul", "div"]); // na zewnątrz: mnożenie lub dzielenie
  const k = pick([2, 3, 4]); // potęga nawiasu (>=2, żeby nie było trywialnie)

  // dobieramy m, p, n z {1..9} tak, aby wynikowy wykładnik był ≥ 1
  let m, p, n, tries = 0;
  while (tries++ < 100) {
    m = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]);
    p = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]);

    if (innerOp === "div" && m < p) continue; // żeby (m - p) nie było ujemne

    const innerExp = innerOp === "mul" ? m + p : m - p; // ≥ 0 (dla dzielenia dopuszczamy 0)
    const powered = innerExp * k; // po potędze nawiasu

    // dobieramy n tak, aby końcowy wykładnik był ≥ 1
    if (outerOp === "div") {
      const maxN = Math.min(9, powered); // nie przekraczamy 9 i powered
      if (maxN < 1) continue; // jeśli powered=0 → próbujemy ponownie
      n = pick(Array.from({ length: maxN }, (_, i) => i + 1));
    } else {
      n = pick([1, 2, 3, 4, 5, 6, 7, 8, 9]); // mnożenie na zewnątrz – zawsze OK
    }

    const target = outerOp === "mul" ? powered + n : powered - n;
    if (target >= 1) {
      // TUTAJ losujemy formy zapisu (tylko jeśli w ogóle jest dzielenie)
      const innerForm =
        innerOp === "div" ? pick(["fraction", "colon"]) : "none";
      const outerForm =
        outerOp === "div" ? pick(["fraction", "colon"]) : "none";

      return {
        type: "PowMixParensTask",
        data: { a, m, p, n, k, innerOp, outerOp, innerForm, outerForm },
      };
    }
  }

  // awaryjnie (nie powinno zajść)
  m = 2;
  p = 1;
  n = 1;
  const innerForm = "none";
  const outerForm = "none";
  return {
    type: "PowMixParensTask",
    data: { a, m, p, n, k, innerOp: "mul", outerOp: "mul", innerForm, outerForm },
  };
}

export function renderTask({ data }) {
  const { a, m, p, n, k, innerOp, outerOp, innerForm, outerForm } = data;

  // W treści zadania dajemy formę dzielenia wybraną w generateTask:
  // • jeśli innerOp === "mul" → zwykły iloczyn
  // • jeśli innerOp === "div" → ":" albo "\dfrac" według innerForm
  const innerLeft =
    innerOp === "mul"
      ? `${factorLatex(a, m)}\\,\\cdot\\,${factorLatex(a, p)}`
      : innerDivisionLatex(a, m, p, innerForm === "fraction");

  const outerRight = `${factorLatex(a, n)}`;

  const fullLeft =
    outerOp === "mul"
      ? `\\big(${innerLeft}\\big)^{${k}}\\,\\cdot\\,${outerRight}`
      : outerDivisionLatex(
          `\\big(${innerLeft}\\big)^{${k}}`,
          outerRight,
          outerForm === "fraction"
        );

  return (
    <div style={{ marginBottom: "12px" }}>
      <div>
        Przedstaw w postaci jednej potęgi o podstawie{" "}
        <InlineMath math={`${a}`} />:
      </div>
      <BlockMath math={fullLeft} />
    </div>
  );
}

export function renderInput(task, userAnswer, onChange, disabled) {
  const handleChange = (ans) => onChange?.(ans);
  return (
    <div>
      <FractionInput
        value={userAnswer}
        onChange={handleChange}
        disabled={disabled}
        placeholder="np. 5^7"
        label=""
        powerPreview={true}
      />
      {/* zbicie domyślnego dolnego marginesu podglądu FractionInput */}
      <div aria-hidden style={{ marginTop: "-16px" }} />
      {/* docelowy odstęp pod całym inputem */}
      <div aria-hidden style={{ height: "12px" }} />
    </div>
  );
}

function parsePowerInput(raw) {
  if (!raw) return null;
  const m = raw.replace(/\s/g, "").match(/^(\d+)\^(\d+)$/);
  if (!m) return null;
  return { base: parseInt(m[1], 10), exp: parseInt(m[2], 10) };
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return false;
  const parsed = parsePowerInput(userAnswer.raw);
  if (!parsed) return false;

  const { a, m, p, n, k, innerOp, outerOp } = data;
  const innerExp = innerOp === "mul" ? m + p : m - p;
  const powered = innerExp * k;
  const target = outerOp === "mul" ? powered + n : powered - n;

  return parsed.base === a && parsed.exp === target;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw)
    return <i>(brak odpowiedzi)</i>;

  const raw = String(userAnswer.raw).trim().replace(/\s+/g, "");
  let latex = raw;
  const match = raw.match(/^(\d+)\^(\d+)$/);
  if (match) latex = `${match[1]}^{${match[2]}}`;

  return <InlineMath math={latex} />;
}

export function renderAnswer({ data }) {
  const { a, m, p, n, k, innerOp, outerOp } = data;
  const innerExp = innerOp === "mul" ? m + p : m - p;
  const powered = innerExp * k;
  const target = outerOp === "mul" ? powered + n : powered - n;
  return <InlineMath math={`${a}^{${target}}`} />;
}

export function renderExplanation({ data }) {
  const { a, m, p, n, k, innerOp, outerOp } = data;

  // W WYJAŚNIENIU zostawiamy „kanoniczną” wersję z dwukropkiem,
  // żeby reguły były czytelne i bez losowania.
  const innerLeft =
    innerOp === "mul"
      ? `${factorLatex(a, m)}\\,\\cdot\\,${factorLatex(a, p)}`
      : `${factorLatex(a, m)}\\,:\\,${factorLatex(a, p)}`;

  const fullLeft =
    outerOp === "mul"
      ? `\\big(${innerLeft}\\big)^{${k}}\\,\\cdot\\,${factorLatex(a, n)}`
      : `\\big(${innerLeft}\\big)^{${k}}\\,:\\,${factorLatex(a, n)}`;

  const signInner = innerOp === "mul" ? "+" : "-";
  const signOuter = outerOp === "mul" ? "+" : "-";

  const lineLatex =
    `${fullLeft}\\;=\\;` +
    `${a}^{(${m}${signInner}${p})\\cdot${k} ${signOuter} ${n}}` +
    `\\;=\\;${a}^{${(innerOp === "mul" ? m + p : m - p)}\\cdot${k} ${signOuter} ${n}}` +
    `\\;=\\;${a}^{${(innerOp === "mul" ? m + p : m - p) * k}${signOuter}${n}}` +
    `\\;=\\;${a}^{${
      (innerOp === "mul" ? m + p : m - p) * k + (outerOp === "mul" ? n : -n)
    }}`;

  return (
    <div style={{ display: "grid", rowGap: "8px" }}>
      <p>
        <strong>Krok 1:</strong> Zasady dla tej samej podstawy:
      </p>
      <BlockMath math={'a^{m}\\cdot a^{n}=a^{m+n}\\quad a^{m}:a^{n}=a^{m-n}\\quad (a^{m})^{n}=a^{m\\cdot n}'} />
      <p>
        <strong>Krok 2:</strong> Podstaw i sprowadź do jednej potęgi:
      </p>
      <BlockMath math={lineLatex} />
    </div>
  );
}
